/**
 * Resolve the route a freshly-authenticated user should land on, based on
 * how far they've progressed through the workflow.
 *
 * Stages → routes:
 *
 *   signed up                       → /connect       (or /confirm if platforms are linked)
 *   connected (creds, no chat)      → /connect
 *   ai (analysis running/recovering)→ /processing/:chatId
 *   ai (refine running/recovering)  → /generate-report/:chatId
 *   ai (compiled_report exists)     → /report/:chatId
 *   review (placeholder shown)      → /report/:chatId
 *   approved | live                 → /report/:chatId
 *
 * NOTE on naming: the workflow stage `signed` means *signed up*.
 *
 * Employees skip this gate — staff portals are out of scope here. We always
 * fall back to the safe default (`/connect`) on any error so login never gets
 * blocked by a transient RPC failure.
 */
import { supabase } from '../lib/supabase';
import { fetchAuditStatus, loadCredentials } from '../api/client';

// A deep link the user hit while logged out, stashed by AuthGuard so login can
// return them to it. Needed for the Shopify claim (`/shopify/claim?shop=…`): a
// Shopify-initiated install lands a NOT-logged-in merchant there, but the
// workflow-based routing below can't reconstruct the `?shop=` param — without
// this the parked store is never linked. Survives the OAuth round-trip via
// localStorage; short TTL + one-shot consume so it never hijacks a later login.
const PENDING_REDIRECT_KEY = 'post_login_redirect';
const PENDING_REDIRECT_TTL_MS = 10 * 60 * 1000;

/** Stash an in-app path (must start with a single '/', never '/login') to
 * return to after login. Best-effort; silently ignores bad input / no storage. */
export function stashPostLoginRedirect(path: string): void {
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/login')) return;
  try {
    localStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify({ path, ts: Date.now() }));
  } catch {
    /* ignore — no storage */
  }
}

/** Read-and-clear the stashed redirect. Returns a safe in-app path or null. */
function consumePostLoginRedirect(): string | null {
  try {
    const raw = localStorage.getItem(PENDING_REDIRECT_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_REDIRECT_KEY);
    const { path, ts } = JSON.parse(raw) as { path?: unknown; ts?: unknown };
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return null;
    if (typeof ts !== 'number' || Date.now() - ts > PENDING_REDIRECT_TTL_MS) return null;
    return path;
  } catch {
    return null;
  }
}

export type WorkflowStage =
  | 'signed'
  | 'connected'
  | 'ai'
  | 'review'
  | 'approved'
  | 'live';

interface LeadWorkflowSnapshot {
  current_stage: WorkflowStage;
  latest_chat_id: string | null;
  latest_review: { status: string } | null;
}

/**
 * Maps the workflow snapshot (and, when an analysis is in flight, the live
 * audit status) to the target route.
 *
 * Returns a path-only string ready for `navigate(...)`.
 */
export async function resolvePostLoginRoute(
  userId: string,
  opts: { isEmployee?: boolean } = {},
): Promise<string> {
  // A deep link stashed while logged out (e.g. the Shopify claim page) always
  // wins — the merchant was mid-flow and we must return them to it.
  const pending = consumePostLoginRedirect();
  if (pending) return pending;

  // Employees don't go through the lead workflow — they drop into the staff
  // portal (or whatever their landing is). Out of scope for this helper.
  if (opts.isEmployee) return '/connect';

  let snap: LeadWorkflowSnapshot | null = null;
  try {
    const { data, error } = await supabase.rpc('get_lead_workflow', {
      p_user_id: userId,
    });
    if (!error && data) {
      snap = data as LeadWorkflowSnapshot;
    }
  } catch {
    // fall through — we'll send them to the safest default below
  }

  const stage = snap?.current_stage;
  const chatId = snap?.latest_chat_id ?? null;

  // ── Terminal stages always show /report (gated content for non-approved) ──
  if (stage === 'review' || stage === 'approved' || stage === 'live') {
    return chatId ? `/report/${chatId}` : '/report';
  }

  // ── 'ai' stage: route based on whether analysis or refine is live ──
  if (stage === 'ai' && chatId) {
    try {
      const status = await fetchAuditStatus(chatId);
      // Renderable dashboard exists → /report (placeholder if review pending)
      if (status.has_deliverables || status.has_renderable_report) {
        return `/report/${chatId}`;
      }
      // Ingestion produced no usable data → the run was halted; send the user to
      // the connect step (not /processing, which would just show the failed run).
      if (status.has_error && status.error_reason === 'no_data' && !status.is_running) {
        return '/connect?audit_error=no_data';
      }
      // Refine running → /generate-report. We detect refine by the 4-stage
      // total reported by the backend (analysis pass uses 7 stages).
      if (status.is_running && status.current_total_stages === 4) {
        return `/generate-report/${chatId}`;
      }
      // Otherwise an analysis pass is live (or recently finished) → /processing
      return `/processing/${chatId}`;
    } catch {
      return `/processing/${chatId}`;
    }
  }

  // ── 'connected': creds saved, no chat yet → /connect ──
  if (stage === 'connected') return '/connect';

  // ── 'signed' (i.e. signed up) or unknown ──
  // Connect comes first now. If platforms are already linked the user has been
  // past that screen, so send them on to Confirm rather than making them walk
  // the grid again. (Collect is skipped deliberately: re-running the fetch on
  // every login would burn API quota for no new information.)
  try {
    const creds = await loadCredentials();
    const hasAny = Object.values(creds).some((keys) => keys && keys.length > 0);
    if (hasAny) return '/confirm';
  } catch {
    /* ignore */
  }
  return '/connect';
}
